/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types, applySnapshot } from 'mobx-state-tree';
import { toJS } from 'mobx';
import { getUniquBulkRequestIds } from '../services/bulkPublishService';
import BulkPublishReqId from './BulkPublishReqId';
import {
  bulkActionFieldNames,
  PUBLISH_STATUS
} from '../constants/bulkActionStatusConstants';
import { toPublishTitleCase } from '../utils';

// Helper function to determine status from a bulk request
function determineStatusFromRequest(request) {
  let rawStatus = PUBLISH_STATUS.QUEUED;

  if (!request) rawStatus = PUBLISH_STATUS.QUEUED;
  else if (
    request.statuses.every(
      status => status.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    )
  ) {
    rawStatus = PUBLISH_STATUS.DELIVERED;
  } else if (
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase()
    ) &&
    request.statuses.length === 4
  ) {
    rawStatus = PUBLISH_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase()
    ) &&
    request.statuses.length === 3
  ) {
    rawStatus = PUBLISH_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.length === 3
  ) {
    rawStatus = PUBLISH_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.length === 3
  ) {
    rawStatus = PUBLISH_STATUS.IN_PROGRESS;
  }
  // Check for exactly the combination of DELIVERED and FAILURE, nothing else
  else if (
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase()
    ) &&
    request.statuses.length === 2
  ) {
    rawStatus = PUBLISH_STATUS.DELIVERED;
  } else if (
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase()
    ) &&
    request.statuses.length === 2
  ) {
    rawStatus = PUBLISH_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.some(
      s => s.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase()
    ) &&
    request.statuses.length === 2
  ) {
    rawStatus = PUBLISH_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      status =>
        status.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase() ||
        status.toLowerCase() === PUBLISH_STATUS.FAILED_TO_PUBLISH.toLowerCase()
    )
  ) {
    rawStatus = PUBLISH_STATUS.FAILURE;
  } else {
    // Default to QUEUED if no other conditions match
    rawStatus = PUBLISH_STATUS.QUEUED;
  }

  return toPublishTitleCase(rawStatus);
}

// Helper function to calculate progress percentage
function calculateProgressFromRequest(request) {
  if (
    !request ||
    !request.statusCounts ||
    !Array.isArray(request.statusCounts)
  ) {
    return 0;
  }

  const total = request.count || 0;
  if (total === 0) return 0;

  const completedCount =
    request.statusCounts.find(
      s => s.status.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    )?.count || 0;
  const errorCount =
    request.statusCounts.find(
      s =>
        s.status.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase() ||
        s.status.toLowerCase() ===
          PUBLISH_STATUS.FAILED_TO_PUBLISH.toLowerCase() ||
        s.status.toLowerCase() === PUBLISH_STATUS.FAILED.toLowerCase()
    )?.count || 0;

  return Math.round(((completedCount + errorCount) / total) * 100);
}

// Helper function to get completed count
function getCompletedCountFromRequest(request) {
  if (
    !request ||
    !request.statusCounts ||
    !Array.isArray(request.statusCounts)
  ) {
    return 0;
  }
  const completedCount =
    request.statusCounts.find(
      s => s?.status.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    )?.count || 0;
  const falilureCount =
    request.statusCounts.find(
      s =>
        s?.status.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase() ||
        s?.status.toLowerCase() ===
          PUBLISH_STATUS.FAILED_TO_PUBLISH.toLowerCase() ||
        s?.status.toLowerCase() === PUBLISH_STATUS.FAILED.toLowerCase()
    )?.count || 0;
  return completedCount + falilureCount;
}
export default types
  .model('BulkPublishReqIdList', {
    bulkPublishReqIds: types.optional(types.array(BulkPublishReqId), []),
    hasMore: types.maybeNull(types.boolean, false),
    totalCount: types.maybeNull(types.number, 0),
    sortBy: types.optional(
      types.string,
      bulkActionFieldNames.PUBLISH_INITIATE_TIME
    ),
    sortDirection: types.optional(types.enumeration(['asc', 'desc']), 'desc'),
    page: types.optional(types.number, 1),
    pageSize: types.optional(types.number, 10)
  })
  .views(self => ({
    get sortedPublishData() {
      const sortableData = [...toJS(self?.bulkPublishReqIds)];

      if (self.sortBy) {
        sortableData.sort((a, b) => {
          if (a[self.sortBy] < b[self.sortBy]) {
            return self.sortDirection === 'asc' ? -1 : 1;
          }
          if (a[self.sortBy] > b[self.sortBy]) {
            return self.sortDirection === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }

      return sortableData;
    }
  }))
  .actions(self => ({
    resetList() {
      applySnapshot(self.bulkPublishReqIds, []);
      self.hasMore = false;
      self.totalCount = 0;
    },
    setBulkRequestIds(bulkRequests) {
      self.bulkPublishReqIds = bulkRequests;
    },
    setHasMore(hasMore) {
      self.hasMore = hasMore;
    },
    setTotalCount(totalCount) {
      self.totalCount = totalCount;
    },
    setNewPageValue(page) {
      self.page = page;
    },

    resetBulkPageIndex() {
      self.page = 1;
    },

    async loadBulkProjects(resetProjects) {
      const apiParams = {
        page: resetProjects ? 1 : self.page,
        pageSize: self.pageSize
      };

      if (resetProjects) {
        self.resetBulkPageIndex();
        self.resetList();
      }
      const {
        bulkRequests,
        hasMore = false,
        totalCount = 0
      } = await getUniquBulkRequestIds(apiParams);

      const newItems = [];
      bulkRequests.forEach(request => {
        // Define default values for fields
        const status = determineStatusFromRequest(request);
        const progress = calculateProgressFromRequest(request);

        newItems.push({
          id: Math.random().toString(36).substring(2, 9),
          operationType: 'Publish',
          title: '',
          userid: request.userid || '',
          publishInitiateTime: request.publishInitiateTime || '',
          startTime: request.startTime || '',
          endTime: request.endTime || '',
          errorDetails: '',
          status,
          progress,
          total: request.count || 0,
          completed: getCompletedCountFromRequest(request),
          bulkRequestId: request.bulkRequestId,
          statuses: request.statuses || []
        });
      });
      self.setBulkRequestIds(newItems);
      self.setHasMore(hasMore);
      self.setTotalCount(totalCount);
    },
    async updateBulkRequestIdsLIst() {
      const updatedPageValue = self.page + 1;
      self.setNewPageValue(updatedPageValue);
      const apiParams = {
        page: updatedPageValue || self.page,
        pageSize: self.pageSize
      };
      const { bulkRequests, hasMore, totalCount } =
        await getUniquBulkRequestIds(apiParams);
      const newItems = [];
      bulkRequests.forEach(request => {
        // Define default values for fields
        const status = determineStatusFromRequest(request);
        const progress = calculateProgressFromRequest(request);

        newItems.push({
          id: Math.random().toString(36).substring(2, 9),
          operationType: 'Publish',
          title: '',
          userid: request.userid || '',
          publishInitiateTime: request.publishInitiateTime || '',
          startTime: request.startTime || '',
          endTime: request.endTime || '',
          errorDetails: '',
          status,
          progress,
          total: request.count || 0,
          completed: getCompletedCountFromRequest(request),
          bulkRequestId: request.bulkRequestId,
          statuses: request.statuses || []
        });
      });
      const currentBulkRequestIds = [
        ...toJS(self.bulkPublishReqIds),
        ...newItems
      ];

      self.setBulkRequestIds(currentBulkRequestIds);
      self.setHasMore(hasMore);
      self.setTotalCount(totalCount);
    },

    setSorting(field) {
      if (self.sortBy === field) {
        // Toggle direction if same field
        self.sortDirection = self.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New field, default to ascending
        self.sortBy = field;
        self.sortDirection = 'asc';
      }
    }
  }));
